/* Who hears from Ledger.
 *
 * The recipient list started life as an Azure app setting, which put "add Rob
 * to the Monday email" behind a portal, a resource group and somebody who knows
 * what a Function App is. It is a preference, so it belongs here.
 *
 * What has deliberately not moved is the mailbox the digest sends *from*. That
 * address is what an Exchange application access policy is pinned to — it is
 * the reason the Function can send as one mailbox and not as everybody — so it
 * stays in Azure where changing it takes tenant admin rather than write access
 * to a SharePoint list. The screen says so rather than leaving somebody hunting
 * for a field that is missing on purpose.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useOnline } from '../../hooks/useOnline';
import { addressList, DIGEST_RECIPIENTS, looksLikeAddress } from '../../lib/settings';
import { PRODUCT } from '../../lib/product';
import { qk } from '../../query/keys';
import { useServices } from '../../services/context';
import { Badge, Button, EmptyState, LoadError, Panel, TableSkeleton } from '../ui';
import s from './screens.module.scss';

export function NotificationsScreen() {
  const svc = useServices();
  const qc = useQueryClient();
  const online = useOnline();

  const settings = useQuery({ queryKey: qk.settings, queryFn: () => svc.settings.all() });

  /* Held as a list here and stored as lines, because a list is what this is.
     One textarea would work and would also let somebody save "rob@hird.co.uk
     and maybe Sarah?" — which reads fine and sends to nobody. */
  const [addresses, setAddresses] = useState<string[]>([]);
  const [entry, setEntry] = useState('');
  const [dirty, setDirty] = useState(false);

  /* Seeded from the server, once, and then left alone. Re-seeding on every
     fetch would take somebody's half-finished edit away underneath them the
     moment another query invalidated this one. */
  useEffect(() => {
    if (settings.data && !dirty) setAddresses(addressList(settings.data[DIGEST_RECIPIENTS]));
  }, [settings.data, dirty]);

  const save = useMutation({

    meta: { ok: 'Digest recipients saved' },
    mutationFn: () => svc.settings.set(DIGEST_RECIPIENTS, addresses.join('\n')),
    onSuccess: () => {
      setDirty(false);
      void qc.invalidateQueries({ queryKey: qk.settings });
      void qc.invalidateQueries({ queryKey: qk.audit });
    },
  });

  const trimmed = entry.trim();
  const already = addresses.some((a) => a.toLowerCase() === trimmed.toLowerCase());
  const valid = looksLikeAddress(trimmed);

  const add = () => {
    if (!valid || already) return;
    setAddresses([...addresses, trimmed]);
    setEntry('');
    setDirty(true);
  };

  const drop = (address: string) => {
    setAddresses(addresses.filter((a) => a !== address));
    setDirty(true);
  };

  return (
    <div className={s.stack}>
      <div className={s.head}>
        <div className={s.grow}>
          <h1>Notifications</h1>
          <p>Who gets the weekly summary of what is overdue and what is due soon.</p>
        </div>
      </div>

      <Panel
        title="Weekly digest"
        sub="Monday morning, or nothing if nobody is listed"
        actions={dirty ? <Badge tone="warn">Unsaved</Badge> : undefined}
      >
        {settings.isLoading ? (
          <TableSkeleton rows={3} />
        ) : settings.isError ? (
          <LoadError error={settings.error} what="settings" />
        ) : (
          <div className={s.stackBox}>
            <div className={s.addRow}>
              <input
                type="email"
                value={entry}
                placeholder="name@hird.co.uk"
                aria-label="Email address"
                onChange={(e) => setEntry(e.target.value)}
                /* Enter adds, because typing an address and pressing Enter is
                   what everybody does and having it submit nothing is a small
                   insult. */
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    add();
                  }
                }}
              />
              <Button icon="ti-plus" disabled={!valid || already} onClick={add}>
                Add
              </Button>
            </div>

            {/* Says which of the two reasons the button is off. "Add" simply
                greyed out looks broken when the address is a duplicate. */}
            {trimmed !== '' && !valid && (
              <div className={s.hint}>That does not look like an email address.</div>
            )}
            {already && <div className={s.hint}>{trimmed} is already on the list.</div>}

            {addresses.length === 0 ? (
              <EmptyState icon="ti-mail-off" title="Nobody is listed">
                With no recipients the digest does not send. Nothing fails and no
                reminder arrives — every date in {PRODUCT.name} then only surfaces
                if somebody opens it.
              </EmptyState>
            ) : (
              <div className={s.chipRow}>
                {addresses.map((a) => (
                  <span key={a} className={s.addressChip}>
                    <i className="ti ti-mail" aria-hidden />
                    {a}
                    <button
                      type="button"
                      aria-label={`Remove ${a}`}
                      title={`Remove ${a}`}
                      onClick={() => drop(a)}
                    >
                      <i className="ti ti-x" aria-hidden />
                    </button>
                  </span>
                ))}
              </div>
            )}

            <div className={s.actions}>
              <Button
                primary
                icon={save.isPending ? 'ti-loader-2' : 'ti-device-floppy'}
                spinning={save.isPending}
                disabled={!online || !dirty || save.isPending}
                onClick={() => save.mutate()}
              >
                {save.isPending ? 'Saving…' : 'Save recipients'}
              </Button>

              {dirty && !save.isPending && (
                <span className={s.bulkNote}>
                  Nothing is sent to a new address until this is saved.
                </span>
              )}
              {!online && <div className={s.hint}>Saving needs a network.</div>}
            </div>

            {save.isError && (
              <div className={`${s.ownedNote} ${s.saveError}`}>
                <i className="ti ti-alert-triangle" aria-hidden />
                <span>
                  {save.error instanceof Error ? save.error.message : 'Could not save.'}
                </span>
              </div>
            )}
          </div>
        )}
      </Panel>

      <Panel title="What it sends, and what it does not">
        <div className={s.bullets}>
          <ul>
            <li>
              <b>Monday at 07:30.</b> What is overdue, what falls due in the next
              thirty days, backup jobs that failed, and anything Intune reports as
              non-compliant.
            </li>
            <li>
              <b>A quiet week still arrives.</b> An email that only turns up when
              something is wrong teaches everybody that silence means fine — and
              silence also means the timer stopped, which is the failure worth
              catching.
            </li>
            <li>
              <b>It links rather than lists.</b> No warranty dates or supplier
              references travel in the email; it says the counts and points back
              here.
            </li>
            <li>
              {/* Said here so nobody goes looking for a field that is missing
                  deliberately. */}
              <b>The sending mailbox is set in Azure, not here.</b> It is the one
              address an Exchange access policy allows the sync to send as, which
              makes it a permission rather than a preference. Changing it needs
              tenant admin — see docs/digest-setup.md.
            </li>
          </ul>
        </div>
      </Panel>
    </div>
  );
}
